import { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import * as usersApi from '@/api/users'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'

function formatLastLogin(iso) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return String(iso)
  }
}

function relativeHint(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const days = Math.floor((Date.now() - then) / 86400000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 14) return `${days} days ago`
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`
  return `${Math.floor(days / 30)} months ago`
}

export function LoginActivitySettings() {
  const { isAdmin } = useAuth()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [users, setUsers] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [showNever, setShowNever] = useState(true)
  const [showAdmins, setShowAdmins] = useState(false)

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [u, c] = await Promise.all([
          usersApi.listUsers(),
          customersApi.listCustomers(),
        ])
        if (!cancelled) {
          setUsers(u)
          setCustomers(c)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load login activity.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  const customerName = useMemo(() => {
    const map = Object.fromEntries(customers.map((c) => [c.id, c.name]))
    return (ids) => {
      if (!ids?.length) return '—'
      return ids.map((id) => map[id] || `Customer #${id}`).join(', ')
    }
  }, [customers])

  const rows = useMemo(() => {
    let list = users.slice()
    if (!showAdmins) {
      list = list.filter((u) => u.role === 'customer')
    }
    if (!showNever) {
      list = list.filter((u) => u.last_login)
    }
    if (filterCustomer) {
      const id = Number(filterCustomer)
      list = list.filter((u) => {
        const ids = u.customer_ids?.length
          ? u.customer_ids
          : u.customer_id != null
            ? [u.customer_id]
            : []
        return ids.includes(id)
      })
    }
    list.sort((a, b) => {
      const at = a.last_login ? new Date(a.last_login).getTime() : 0
      const bt = b.last_login ? new Date(b.last_login).getTime() : 0
      if (bt !== at) return bt - at
      return String(a.full_name || a.email).localeCompare(
        String(b.full_name || b.email),
      )
    })
    return list
  }, [users, showAdmins, showNever, filterCustomer])

  const neverCount = useMemo(
    () =>
      users.filter((u) => u.role === 'customer' && !u.last_login).length,
    [users],
  )

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <SettingsShell title="Login activity" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Login activity
          </h1>
          <p className="mt-2 max-w-2xl text-brand-muted">
            See when customer users last signed in to view their knowledge base.
            Updated on each successful login (including when 2FA is remembered on
            that browser).
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white/90 p-4 shadow-sm">
          <label className="block text-sm">
            <span className="text-brand-muted">Customer</span>
            <select
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="mt-1 block min-w-[12rem] rounded border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-brand">
            <input
              type="checkbox"
              checked={showNever}
              onChange={(e) => setShowNever(e.target.checked)}
            />
            Include never signed in
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-brand">
            <input
              type="checkbox"
              checked={showAdmins}
              onChange={(e) => setShowAdmins(e.target.checked)}
            />
            Include admin accounts
          </label>
          {neverCount > 0 && (
            <p className="pb-2 text-xs text-amber-800">
              {neverCount} customer user{neverCount === 1 ? '' : 's'} have never
              signed in
            </p>
          )}
        </div>

        {loading ? (
          <p className="text-brand-muted">Loading…</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white/90 shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-brand-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Customers</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Last login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-brand-muted"
                    >
                      No users match these filters.
                    </td>
                  </tr>
                )}
                {rows.map((u) => {
                  const when = formatLastLogin(u.last_login)
                  const rel = relativeHint(u.last_login)
                  const ids = u.customer_ids?.length
                    ? u.customer_ids
                    : u.customer_id != null
                      ? [u.customer_id]
                      : []
                  return (
                    <tr
                      key={u.id}
                      className={!u.is_active ? 'opacity-60' : undefined}
                    >
                      <td className="px-4 py-3 align-top">
                        <p className="font-medium text-brand">{u.full_name}</p>
                        <p className="text-xs text-brand-muted">{u.email}</p>
                      </td>
                      <td className="px-4 py-3 align-top capitalize">{u.role}</td>
                      <td className="px-4 py-3 align-top text-brand-muted">
                        {customerName(ids)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {u.is_active ? (
                          <span className="text-xs text-emerald-800">Active</span>
                        ) : (
                          <span className="text-xs text-slate-500">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {when ? (
                          <>
                            <p className="text-brand">{when}</p>
                            {rel && (
                              <p className="text-xs text-brand-muted">{rel}</p>
                            )}
                          </>
                        ) : (
                          <span className="text-xs font-medium text-amber-800">
                            Never signed in
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SettingsShell>
  )
}
